import React from 'react';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Divider,
  Avatar
} from '@mui/material';
import { useSelector, TypedUseSelectorHook } from 'react-redux';
import { RootState } from 'app/store';
import { UIMappedInvoice } from 'types/ndisSection/invoiceTypes';

interface PrintComponentProps {
  invoiceData: UIMappedInvoice;
}

const PrintComponent: React.FC<PrintComponentProps> = ({ invoiceData }) => {
  const useTypedSelector: TypedUseSelectorHook<RootState> = useSelector;
  const { organization, client } = useTypedSelector(
    (state) => state.invoice || { organization: null, client: null }
  );

  const a4Width = 210;
  const a4Height = 297;
  const margin = 10;

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return '#4caf50';
      case 'unpaid':
        return '#f44336';
      default:
        return '#000000';
    }
  };

  // Calculate service period from shifts data
  const shifts = invoiceData.shifts || [];
  const serviceDates = shifts
    .map((shift) => shift.supports_delivered_from)
    .filter((date): date is string => !!date); // Ensure we only have valid strings

  const getValidDate = (dateString: string | undefined): Date | null => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? null : date;
  };

  const servicePeriodStart =
    serviceDates.length > 0
      ? new Date(Math.min(...serviceDates.map((date) => new Date(date).getTime())))
      : null;
  const servicePeriodEnd =
    serviceDates.length > 0
      ? new Date(Math.max(...serviceDates.map((date) => new Date(date).getTime())))
      : null;

  const formatDate = (date: Date | string | null | undefined) => {
    if (!date) return 'N/A';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return 'N/A';
    return dateObj.toLocaleDateString('en-AU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Calculate total
  const total = shifts.reduce((total, shift) => total + (shift.Total_amount || 0), 0);

  // Validate base64 strings
  const isValidBase64 = (str: string | undefined) => {
    if (!str) return false;
    try {
      atob(str);
      return true;
    } catch {
      return false;
    }
  };

  return (
    <Box
      sx={{
        width: `${a4Width}mm`,
        minHeight: `${a4Height}mm`,
        padding: `${margin}mm`,
        boxSizing: 'border-box',
        backgroundColor: 'white',
        fontFamily: '"Helvetica Neue", Arial, sans-serif',
        color: '#333',
        '@media print': {
          border: 'none'
        }
      }}
    >
      {/* Header section */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: '10mm'
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 'bold',
              color: '#1976d2',
              mb: '3mm'
            }}
          >
            INVOICE
          </Typography>
          <Typography variant="body1" sx={{ mb: '1mm' }}>
            <strong>Invoice #:</strong> {invoiceData.invoiceNumber}
          </Typography>
          <Typography variant="body1" sx={{ mb: '1mm' }}>
            <strong>Date Issued:</strong> {formatDate(invoiceData.date)}
          </Typography>
          <Typography variant="body1" sx={{ mb: '1mm' }}>
            <strong>Due Date:</strong> {formatDate(invoiceData.dueDate)}
          </Typography>
          <Typography variant="body1" sx={{ mb: '1mm' }}>
            <strong>Business:</strong> {organization?.business_name || 'N/A'}
          </Typography>
          <Typography variant="body1">
            <strong>ABN:</strong> {organization?.abn || invoiceData.paymentDetails?.ABN || 'N/A'}
          </Typography>
          {organization?.business_phone && (
            <Typography variant="body1" sx={{ mb: '1mm' }}>
              <strong>Phone:</strong> {organization.business_phone}
            </Typography>
          )}
          {organization?.business_email && (
            <Typography variant="body1" sx={{ mb: '1mm' }}>
              <strong>Email:</strong> {organization.business_email}
            </Typography>
          )}
        </Box>
        <Avatar
          src={
            organization?.icon_path && isValidBase64(organization.icon_path)
              ? `data:image/png;base64,${organization.icon_path}`
              : undefined
          }
          variant="rounded"
          sx={{ width: '50mm', height: 'auto', bgcolor: '#e0e0e0' }}
        />
      </Box>

      <Divider sx={{ my: '5mm', borderColor: '#e0e0e0' }} />

      {/* Billing and Payment Info */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mb: '8mm'
        }}
      >
        <Box sx={{ width: '45%' }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
              mb: '2mm',
              color: '#1976d2'
            }}
          >
            BILL TO:
          </Typography>
          <Typography variant="body1" sx={{ mb: '1mm' }}>
            <strong>Client:</strong> {client?.client_name || invoiceData.customerName || 'N/A'}
          </Typography>
          {client?.ndis_number && (
            <Typography variant="body1" sx={{ mb: '1mm' }}>
              <strong>NDIS Number:</strong> {client.ndis_number}
            </Typography>
          )}
          <Typography variant="body1" sx={{ mb: '1mm' }}>
            <strong>Address:</strong> {client?.address || invoiceData.address || 'N/A'}
          </Typography>
          {/* <Typography variant="body1">
            {client?.address ? client.address : invoiceData.cityStateZip || 'N/A'}
          </Typography> */}
          {client?.email && (
            <Typography variant="body1" sx={{ mb: '1mm' }}>
              <strong>Fund Manager Email:</strong> {client.manager_email}
            </Typography>
          )}
          {client?.phone && (
            <Typography variant="body1" sx={{ mb: '1mm' }}>
              <strong>Phone:</strong> {client.phone}
            </Typography>
          )}
        </Box>
        <Box sx={{ width: '45%', textAlign: 'left' }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
              mb: '2mm',
              color: '#1976d2'
            }}
          >
            PAYMENT DETAILS:
          </Typography>
          <Typography variant="body1" sx={{ mb: '1mm' }}>
            <strong>BSB:</strong> {organization?.bsb || invoiceData.paymentDetails?.BSB || 'N/A'}
          </Typography>
          <Typography variant="body1" sx={{ mb: '1mm' }}>
            <strong>Account:</strong>{' '}
            {organization?.account_number || invoiceData.paymentDetails?.Account || 'N/A'}
          </Typography>
          {/* <Typography variant="body1">
            <strong>ABN:</strong> {organization?.abn || invoiceData.paymentDetails?.ABN || 'N/A'}
          </Typography>
          {organization?.business_phone && (
            <Typography variant="body1" sx={{ mb: '1mm' }}>
              <strong>Phone:</strong> {organization.business_phone}
            </Typography>
          )}
          {organization?.business_email && (
            <Typography variant="body1" sx={{ mb: '1mm' }}>
              <strong>Email:</strong> {organization.business_email}
            </Typography>
          )} */}
        </Box>
      </Box>

      {/* Service Period */}
      <Box
        sx={{
          backgroundColor: '#f5f5f5',
          p: '2mm',
          mb: '5mm',
          borderRadius: '2mm'
        }}
      >
        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
          SERVICE PERIOD: {formatDate(servicePeriodStart)} TO {formatDate(servicePeriodEnd)}
        </Typography>
      </Box>

      {/* Shifts Table */}
      <TableContainer sx={{ mb: '8mm' }}>
        <Table sx={{ borderCollapse: 'collapse' }}>
          <TableHead sx={{ backgroundColor: '#1976d2' }}>
            <TableRow>
              <TableCell
                sx={{
                  color: 'white',
                  fontWeight: 'bold',
                  padding: '2mm',
                  border: '1px solid #e0e0e0'
                }}
              >
                DESCRIPTION
              </TableCell>
              <TableCell
                sx={{
                  color: 'white',
                  fontWeight: 'bold',
                  padding: '2mm',
                  border: '1px solid #e0e0e0',
                  textAlign: 'right',
                  width: '15%'
                }}
              >
                HOURS
              </TableCell>
              <TableCell
                sx={{
                  color: 'white',
                  fontWeight: 'bold',
                  padding: '2mm',
                  border: '1px solid #e0e0e0',
                  textAlign: 'right',
                  width: '15%'
                }}
              >
                RATE PER HOUR
              </TableCell>
              <TableCell
                sx={{
                  color: 'white',
                  fontWeight: 'bold',
                  padding: '2mm',
                  border: '1px solid #e0e0e0',
                  textAlign: 'center',
                  width: '15%'
                }}
              >
                DATE
              </TableCell>
              <TableCell
                sx={{
                  color: 'white',
                  fontWeight: 'bold',
                  padding: '2mm',
                  border: '1px solid #e0e0e0',
                  textAlign: 'right',
                  width: '15%'
                }}
              >
                TOTAL
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shifts.map((shift, index) => (
              <TableRow key={index}>
                <TableCell sx={{ padding: '2mm', border: '1px solid #e0e0e0' }}>
                  {shift.support_item_name || 'N/A'}
                  <br />
                  <small>Support Worker: {shift.support_worker || 'N/A'}</small>
                </TableCell>
                <TableCell sx={{ padding: '2mm', border: '1px solid #e0e0e0', textAlign: 'right' }}>
                  {shift.Planned_Hours?.toFixed(2) || '0.00'}
                </TableCell>
                <TableCell sx={{ padding: '2mm', border: '1px solid #e0e0e0', textAlign: 'right' }}>
                  ${shift.unit_price?.toFixed(2) || '0.00'}
                </TableCell>
                <TableCell
                  sx={{ padding: '2mm', border: '1px solid #e0e0e0', textAlign: 'center' }}
                >
                  {formatDate(shift.supports_delivered_from)}
                </TableCell>
                <TableCell sx={{ padding: '2mm', border: '1px solid #e0e0e0', textAlign: 'right' }}>
                  ${shift.Total_amount?.toFixed(2) || '0.00'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Totals Section */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: '10mm' }}>
        <Box sx={{ width: '50%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '2mm' }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
              TOTAL:
            </Typography>
            <Typography variant="body1">${total.toFixed(2)}</Typography>
          </Box>
          <Divider sx={{ my: '2mm', borderColor: '#e0e0e0' }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '2mm' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              TOTAL DUE:
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              ${total.toFixed(2)}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          bottom: `${margin}mm`,
          left: `${margin}mm`,
          right: `${margin}mm`,
          textAlign: 'center',
          pt: '5mm',
          borderTop: '1px solid #e0e0e0',
          marginTop: '5px'
        }}
      >
        <Typography variant="body2" sx={{ color: '#757575' }}>
          Thank you for your business!
        </Typography>
        <Typography variant="body2" sx={{ color: '#757575', mt: '1mm' }}>
          Invoice Status:{' '}
          <span
            style={{
              // color: getStatusColor('unpaid' || invoiceData.status || ''),
              color: getStatusColor('unpaid'),
              fontWeight: 'bold'
            }}
          >
            {/* {('unpaid' || invoiceData.status || 'N/A').toUpperCase()} */}
            {'unpaid'.toUpperCase()}
          </span>
        </Typography>
        {organization?.website && (
          <Typography variant="body2" sx={{ color: '#757575', mt: '1mm' }}>
            Visit us at: <a href={organization.website}>{organization.website}</a>
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default PrintComponent;
